import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { Input, Select } from '../../../components/ui/Input';
import { DataTable } from '../../../components/admin/DataTable';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Card';
import { api } from '../../../lib/api';
import { ORDER_STATUSES, ORDER_STATUS_LABELS, type Order, type OrderStatus } from '@bharat-consultancy/shared';

export default function OrderList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(() => {
      api.admin.orders
        .list({ search, status: statusFilter || undefined })
        .then((res) => setOrders(res.items))
        .catch(() => setOrders([]))
        .finally(() => setIsLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, statusFilter]);

  return (
    <>
      <SEOHead title="Orders — Admin" description="" canonicalPath="/admin/orders" />

      <h1 className="font-display text-2xl font-bold text-ink">Orders</h1>

      <div className="mt-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search by name, email, order #..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as OrderStatus | '')} className="max-w-xs">
          <option value="">All Statuses</option>
          {ORDER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {ORDER_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <Spinner className="h-6 w-6 text-seal-blue" />
        ) : (
          <DataTable
            columns={[
              { header: 'Order #', render: (o) => <span className="font-mono text-xs">{o.orderNumber}</span> },
              { header: 'Customer', render: (o) => o.contactName },
              { header: 'Service', render: (o) => o.service?.title ?? '—' },
              { header: 'Amount', render: (o) => `₹${o.totalAmount.toLocaleString('en-IN')}` },
              { header: 'Status', render: (o) => <StatusBadge status={o.status} /> },
              { header: 'Date', render: (o) => new Date(o.createdAt).toLocaleDateString('en-IN') },
            ]}
            rows={orders}
            keyExtractor={(o) => o.id}
            onRowClick={(o) => navigate(`/admin/orders/${o.id}`)}
            emptyMessage="No orders found."
          />
        )}
      </div>
    </>
  );
}
